import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { StatusLegend } from '../components/StatusLegend';
import { useCompletion } from '../contexts/CompletionContext';
import { batchSessions, measurement, operatingNotes, repurposing } from '../data/plan';
import { countdownLabel, getStatus, statusStyles } from '../utils/status';
export function Production() {
  const {
    isDone,
    toggle
  } = useCompletion();
  return <div data-id="element-238" data-mp="3j0gb2z5neoqa">
      <PageHeader eyebrow="Four sessions, eight weeks" title="Production & measurement" description="Each batching session produces the assets for the block ahead, so nobody is designing a countdown graphic the morning it goes out. Tick items as they land — cards stay in place and grey out." data-id="element-239" data-mp="3ntj3h3yovs4w" />

      <div className="mb-5" data-id="element-240" data-mp="6kmgj0s4r42p">
        <StatusLegend data-id="element-241" data-mp="6lxh6rqa3vpl" />
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-id="element-242" data-mp="6nptwwqctaem">
        {batchSessions.map(session => {
        const sessionItems = session.items.map((item, i) => ({
          id: `${session.id}-${i}`,
          label: item
        }));
        const doneItems = sessionItems.filter(item => isDone(item.id)).length;
        const sessionDone = doneItems === sessionItems.length;
        const status = getStatus(session.due, sessionDone);
        const s = statusStyles[status];
        return <section key={session.id} className={`relative overflow-hidden rounded-2xl border pl-5 pr-4 py-4 shadow-panel transition-colors duration-300 ${s.border} ${s.surface}`} data-id="element-243" data-mp="pgboldpiyuxm">
              <span className={`absolute inset-y-0 left-0 w-[3px] ${s.rail}`} aria-hidden="true" data-id="element-244" data-mp="bn2etbxo07ek" />

              <div className="flex items-start justify-between gap-3" data-id="element-245" data-mp="184glq2bofhng">
                <div data-id="element-246" data-mp="186tc93vi30c4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-faint" data-id="element-247" data-mp="13e8ke7w5mxb8">{session.window}</p>
                  <h2 className={`mt-1 text-[15px] font-semibold tracking-tight ${sessionDone ? 'text-faint' : 'text-chalk'}`} data-id="element-248" data-mp="2xlasdzreyrjm">
                    {session.title}
                  </h2>
                </div>
                <span className={`shrink-0 font-mono text-[10px] ${s.text}`} data-id="element-249" data-mp="2nwssrluveqhk">
                  {sessionDone ? 'Done' : countdownLabel(session.due)}
                </span>
              </div>

              <p className="mt-2 font-mono text-[11px] text-mute" data-id="element-250" data-mp="2eut9emwdvabg">
                Covers {session.covers} · {doneItems}/{sessionItems.length}
              </p>

              <ul className="mt-3 space-y-1" data-id="element-251" data-mp="2js2yg1fv1jyr">
                {sessionItems.map(item => {
              const checked = isDone(item.id);
              return <li key={item.id} data-id="element-252" data-mp="29wrrw6elvj23">
                      <button type="button" role="checkbox" aria-checked={checked} onClick={() => toggle(item.id)} className="flex w-full gap-2 rounded-lg px-1.5 py-1.5 text-left transition-colors hover:bg-ink-800/60" data-id="element-253" data-mp="35sbpdgde4zv">
                        <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${checked ? 'border-ink-500 bg-ink-600 text-chalk' : 'border-ink-600 text-transparent'}`} data-id="element-254" data-mp="1npnqkyzx1k10">
                          <CheckIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-255" data-mp="27btvgwroyazh" />
                        </span>
                        <motion.span layout="position" className={`text-xs leading-relaxed ${checked ? 'text-faint line-through decoration-ink-500' : 'text-mute'}`} data-id="element-256" data-mp="2lbcosjfufhrq">
                          {item.label}
                        </motion.span>
                      </button>
                    </li>;
            })}
              </ul>
            </section>;
      })}
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-2" data-id="element-257" data-mp="ni0l4vjvsz31">
        <Panel eyebrow="One asset, many channels" title="Repurposing map" bodyClassName="px-0 py-0" data-id="element-258" data-mp="sdfsd4ndvwqh">
          <ul className="divide-y divide-ink-700/70" data-id="element-259" data-mp="xf53sleiqcf3">
            {repurposing.map(r => <li key={r.hero} className="px-5 py-3.5" data-id="element-260" data-mp="12blvtqou9axg">
                <p className="flex items-center gap-2 text-[13px] font-medium text-chalk" data-id="element-261" data-mp="4pepoccvzaiz">
                  {r.hero}
                  <ArrowRightIcon className="h-3.5 w-3.5 text-faint" aria-hidden="true" data-id="element-262" data-mp="9ng9izi4zdvi" />
                </p>
                <p className="mt-1 text-xs leading-relaxed text-mute" data-id="element-263" data-mp="emb0wpu66ar8">{r.into}</p>
              </li>)}
          </ul>
        </Panel>

        <div className="space-y-4" data-id="element-264" data-mp="jg5innq7sfno">
          <Panel eyebrow="By product and campaign, not just platform" title="Measurement notes" data-id="element-265" data-mp="3i4kuzhhng359">
            <div className="space-y-5" data-id="element-266" data-mp="3n2m9rq81lgax">
              {measurement.map(m => <div key={m.group} data-id="element-267" data-mp="2481f1rhsgx3x">
                  <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-faint" data-id="element-268" data-mp="24700ve5h7uhz">{m.group}</p>
                  <ul className="mt-2 space-y-2" data-id="element-269" data-mp="207r5k4usfokb">
                    {m.points.map(p => <li key={p} className="border-l border-ink-600 pl-3 text-xs leading-relaxed text-mute" data-id="element-270" data-mp="1v61u4o3nl8vp">
                        {p}
                      </li>)}
                  </ul>
                </div>)}
            </div>
          </Panel>

          <Panel eyebrow="Standing playbook" title="Operating notes" data-id="element-271" data-mp="1gi6z3126asfg">
            <ul className="space-y-3" data-id="element-272" data-mp="1cgv8as7kc15o">
              {operatingNotes.map(n => <li key={n} className="text-xs leading-relaxed text-mute" data-id="element-273" data-mp="1ceheemomcuzp">
                  {n}
                </li>)}
            </ul>
          </Panel>
        </div>
      </div>
    </div>;
}